import type { ChangeEvent } from 'react';

/**
 * Domestication — status, date and the document. Optional when an indent is
 * raised, mandatory before a container gates in (the gate says so). Used on
 * Create Indent and on the indent's Documents tab.
 */
export const DOMESTICATION_STATUSES: Array<{ value: string; label: string; needsDoc: boolean; needsDate: boolean }> = [
  { value: 'Domesticated', label: 'Domesticated', needsDoc: true, needsDate: true },
  { value: 'Non-domesticated', label: 'Non-domesticated', needsDoc: true, needsDate: false },
  { value: 'Export', label: 'Export', needsDoc: true, needsDate: false },
  { value: 'Local', label: 'Local (no domestication needed)', needsDoc: false, needsDate: false },
  { value: 'NA', label: 'Not applicable', needsDoc: false, needsDate: false },
];

export interface DomesticationValue { status: string; date: string; file: File | null; existingName?: string }
export const EMPTY_DOMESTICATION: DomesticationValue = { status: '', date: '', file: null };

export default function DomesticationFields({ value, onChange }: { value: DomesticationValue; onChange: (v: DomesticationValue) => void }) {
  const meta = DOMESTICATION_STATUSES.find((s) => s.value === value.status);
  const set = (patch: Partial<DomesticationValue>) => onChange({ ...value, ...patch });
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
      <div>
        <label className="pc-text-muted" style={{ fontSize: '0.8rem', fontWeight: 600 }}>Domestication status</label>
        <select className="pc-input pc-shadow-sm" value={value.status} onChange={(e) => set({ status: e.target.value })}>
          <option value="">— Not decided yet —</option>
          {DOMESTICATION_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>
      <div>
        <label className="pc-text-muted" style={{ fontSize: '0.8rem', fontWeight: 600 }}>Domestication date{meta?.needsDate ? ' *' : ''}</label>
        <input type="date" className="pc-input pc-shadow-sm" value={value.date} disabled={!value.status || value.status === 'NA' || value.status === 'Local'} onChange={(e) => set({ date: e.target.value })} />
      </div>
      <div>
        <label className="pc-text-muted" style={{ fontSize: '0.8rem', fontWeight: 600 }}>Domestication document{meta?.needsDoc ? ' *' : ''}</label>
        <input type="file" className="pc-input pc-shadow-sm" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx" onChange={(e: ChangeEvent<HTMLInputElement>) => set({ file: e.target.files?.[0] || null })} />
        {value.existingName && !value.file && <div className="pc-text-muted" style={{ fontSize: '0.75rem', marginTop: 2 }}>On file: {value.existingName}</div>}
      </div>
      <div className="pc-text-muted" style={{ fontSize: '0.78rem', gridColumn: '1 / -1' }}>
        Optional now — but a container cannot gate in until this is on record{meta?.needsDoc ? `: for “${meta.label}” the document${meta.needsDate ? ' and the date are' : ' is'} required` : ''}. You can add it later under Indent → Documents.
      </div>
    </div>
  );
}
